import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getInterstitialCampaign, getInterstitialCampaignStats, getInterstitialCampaignUserStats } from '../Api';
import type { InterstitialCampaignStatsDto, InterstitialUserStatsRowDto } from '../models/dtos/InterstitialCampaignDto';

export default function InterstitialCampaignStatsPage() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [name, setName] = React.useState('');
    const [stats, setStats] = React.useState<InterstitialCampaignStatsDto | null>(null);
    const [userRows, setUserRows] = React.useState<InterstitialUserStatsRowDto[]>([]);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [campaign, campaignStats, users] = await Promise.all([
                getInterstitialCampaign(id),
                getInterstitialCampaignStats(id),
                getInterstitialCampaignUserStats(id),
            ]);
            if (campaign == null || campaignStats == null) {
                setError('Could not load campaign stats.');
            } else {
                setName(campaign.name);
                setStats(campaignStats);
                setUserRows(users ?? []);
            }
            setLoading(false);
        };
        load();
    }, [id]);

    if (loading) {
        return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }

    if (error || stats == null) {
        return <Alert severity="error" sx={{ m: 3 }}>{error ?? 'Unknown error'}</Alert>;
    }

    const ctrPercent = (stats.ctr * 100).toFixed(2);

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Typography variant="h4">Stats: {name}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography variant="overline">Impressions</Typography><Typography variant="h5">{stats.totalImpressions}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography variant="overline">Clicks</Typography><Typography variant="h5">{stats.totalClicks}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography variant="overline">Unique users</Typography><Typography variant="h5">{stats.uniqueUsers}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography variant="overline">CTR</Typography><Typography variant="h5">{ctrPercent}%</Typography></CardContent></Card>
                    </Grid>
                </Grid>

                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Per user</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>User ID</TableCell>
                                    <TableCell align="right">Impressions</TableCell>
                                    <TableCell align="right">Clicks</TableCell>
                                    <TableCell>Last shown</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userRows.map(row => (
                                    <TableRow key={row.userRef}>
                                        <TableCell>{row.userRef}</TableCell>
                                        <TableCell align="right">{row.impressionsCount}</TableCell>
                                        <TableCell align="right">{row.clicksCount}</TableCell>
                                        <TableCell>{row.lastShownAt ? new Date(row.lastShownAt).toLocaleString() : '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {userRows.length === 0 && (
                                    <TableRow><TableCell colSpan={4}>No data yet.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <Button variant="outlined" onClick={() => navigate('/marketing/interstitials')}>Back to campaigns</Button>
            </Stack>
        </Box>
    );
}
